import { useEffect, useRef } from "react";
import * as Immutable from "immutable";
import URI from "urijs";
import View from "views/logic/views/View";
import { filtersToStreamSet, filtersToStreamCategorySet } from "views/logic/queries/Query";
import { isTypeRelativeWithStartOnly, isTypeRelativeWithEnd } from "views/typeGuards/timeRange";
import useViewType from "views/hooks/useViewType";
import useCurrentQuery from "views/logic/queries/useCurrentQuery";
import useHistory from "routing/useHistory";
const extractTimerangeParams = (timerange) => {
  const { type } = timerange;
  const result = { rangetype: type };
  const formatResult = (paramsObject) => Object.entries(paramsObject);
  switch (timerange.type) {
    case "relative":
      if (isTypeRelativeWithStartOnly(timerange)) {
        return formatResult({ ...result, relative: timerange.range });
      }
      if (isTypeRelativeWithEnd(timerange)) {
        if ("to" in timerange) {
          return formatResult({ ...result, from: timerange.from, to: timerange.to });
        }
        return formatResult({ ...result, from: timerange.from });
      }
      return formatResult(result);
    case "keyword":
      return formatResult({ ...result, keyword: timerange.keyword });
    case "absolute":
      return formatResult({ ...result, from: timerange.from, to: timerange.to });
    default:
      return Object.entries(result);
  }
};
const uriForView = (viewType, query, searchQuery) => {
  if (viewType !== View.Type.Search) {
    return void 0;
  }
  if (searchQuery) {
    const {
      query: { query_string: queryString },
      timerange,
      filter = Immutable.Map()
    } = searchQuery;
    const baseUri = new URI(query).setSearch("q", queryString).removeQuery("from").removeQuery("to").removeQuery("keyword").removeQuery("relative");
    const uriWithTimerange = extractTimerangeParams(timerange).reduce(
      (prev, [key, value]) => prev.setSearch(key, String(value)),
      baseUri
    );
    const currentStreams = filtersToStreamSet(filter);
    const currentStreamCategories = filtersToStreamCategorySet(filter);
    const uriWithStreams = currentStreams.isEmpty() ? uriWithTimerange.removeSearch("streams") : uriWithTimerange.setSearch("streams", currentStreams.join(","));
    const uri = currentStreamCategories.isEmpty() ? uriWithStreams.removeSearch("stream_categories") : uriWithStreams.setSearch("stream_categories", currentStreamCategories.join(","));
    return uri.toString();
  }
  return void 0;
};
function canonicalizeUri(uri) {
  const [path, queryString = ""] = uri.split("?", 2);
  if (!queryString) return path;
  const params = queryString.split("&").filter(Boolean).map((part) => {
    const [k, v = ""] = part.split("=", 2);
    return [k, v];
  }).sort(([k1, v1], [k2, v2]) => k1 === k2 ? v1.localeCompare(v2) : k1.localeCompare(k2));
  const canonicalQuery = params.map(([k, v]) => `${k}=${v}`).join("&");
  return `${path}?${canonicalQuery}`;
}
function isSameUri(a, b) {
  return canonicalizeUri(a) === canonicalizeUri(b);
}
const useSyncWithQueryParameters = (currentUri) => {
  const viewType = useViewType();
  const currentQuery = useCurrentQuery();
  const history = useHistory();
  const lastSyncedUriRef = useRef(currentUri);
  const isFirstSyncRef = useRef(true);
  useEffect(() => {
    const uriForCurrentView = uriForView(viewType, currentUri, currentQuery);
    if (!uriForCurrentView) {
      return;
    }
    const currentUriMatchesCurrentView = isSameUri(currentUri, uriForCurrentView);
    const currentUriMatchesLastSync = isSameUri(currentUri, lastSyncedUriRef.current);
    if (!currentUriMatchesLastSync && !currentUriMatchesCurrentView) {
      return;
    }
    if (!currentUriMatchesCurrentView) {
      const updateHistory = isFirstSyncRef.current ? history.replace : history.push;
      updateHistory(uriForCurrentView);
    }
    isFirstSyncRef.current = false;
    lastSyncedUriRef.current = uriForCurrentView;
  }, [currentQuery, history, currentUri, viewType]);
};
export default useSyncWithQueryParameters;
